import { useRef, useState } from "react"
import { Download, QrCode } from "lucide-react"
import QRCode from "react-qr-code"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  QR_DOWNLOAD_FORMATS,
  qrFileName,
  qrSheetBlob,
  readQrSvg,
  saveBlob,
  type QrDownloadFormat,
} from "@/features/staff/qr-download"
import { patchInline, postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { t } from "@/lib/i18n"
import { toast } from "sonner"

export type StaticInvite = {
  id: number
  token: string
  url: string
} | null

type PrintedQrCardProps = {
  tableExternalId: string
  tableName: string
  locationName: string
  staticInvite: StaticInvite
  className?: string
}

/**
 * The table's printed QR: the MVP's default door. Mints idempotently
 * (`Staff::Location::Manage::TablesController#static_invite` returns the same
 * token on a second click, since the paper may already be printed) and, once
 * minted, shows exactly what goes on the sheet — the QR, the restaurant name
 * and the table name.
 *
 * The card used to offer `window.print()` and nothing else, which assumed the
 * printer is attached to the device holding the tablet. It usually is not: the
 * sheet gets sent to a print shop, dropped into a table-tent template, or put
 * on a laminator that lives somewhere else entirely, and none of those accept a
 * browser print dialog. Downloading a file is what all three want.
 *
 * The `print:` classes stay. Nothing points at them any more, but the browser's
 * own Cmd+P has not gone anywhere, and a card that printed cleanly yesterday
 * should not start printing the staff dashboard around it today.
 *
 * Generation is entirely client-side. See `@/features/staff/qr-download` for
 * why nothing is stored and why the file is a sheet rather than a bare code.
 */
export function PrintedQrCard({
  tableExternalId,
  tableName,
  locationName,
  staticInvite,
  className,
}: PrintedQrCardProps) {
  const [generating, setGenerating] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [downloading, setDownloading] = useState<QrDownloadFormat | null>(null)
  const qrRef = useRef<HTMLDivElement>(null)

  const download = (format: QrDownloadFormat) => {
    const source = readQrSvg(qrRef.current)

    // The QR is rendered directly above this button, so this is unreachable in
    // practice — which is exactly why it must not fail silently if it ever does.
    if (!source) {
      toast.error(t("ui.react.pages.staff.location.manage.tables.show.download_qr_failed"))
      return
    }

    setDownloading(format)
    qrSheetBlob({ ...source, locationName, tableName }, format)
      .then((blob) => saveBlob(blob, qrFileName(locationName, tableName, format)))
      .catch(() => toast.error(t("ui.react.pages.staff.location.manage.tables.show.download_qr_failed")))
      .finally(() => setDownloading(null))
  }

  const generate = () => {
    setGenerating(true)
    postInline(`/manage/tables/${tableExternalId}/static_invite`)
      .then(toastInlineUpdateSuccess)
      .catch(toastInlineUpdateError)
      .finally(() => setGenerating(false))
  }

  const revoke = () => {
    if (!staticInvite) return

    setRevoking(true)
    patchInline(`/invites/${staticInvite.id}/revoke`, { reason: "manual_staff_revoke" })
      .then(toastInlineUpdateSuccess)
      .catch(toastInlineUpdateError)
      .finally(() => setRevoking(false))
  }

  return (
    <Card className={className} data-testid="printed-qr-card">
      <CardHeader className="print:hidden">
        <CardTitle>{t("ui.react.pages.staff.location.manage.tables.show.printed_qr")}</CardTitle>
        <CardDescription>{t("ui.react.pages.staff.location.manage.tables.show.printed_qr_help")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 print:flex print:flex-col print:items-center print:justify-center print:gap-4 print:py-24">
        {staticInvite ? (
          <>
            {/* The ref is on the wrapper, not on `QRCode`: react-qr-code
                forwards no ref, and the export reads the rendered `<svg>` back
                out of the DOM so the file is the code the screen showed rather
                than a second encoding of the same URL. */}
            <div
              ref={qrRef}
              className="mx-auto w-fit rounded-lg border bg-white p-4 print:border-0 print:p-0"
            >
              <QRCode value={staticInvite.url} size={200} />
            </div>

            {/* Only visible on the printed sheet: the QR alone, glued to a
                table, says nothing about which restaurant or which table it
                belongs to. */}
            <div className="hidden text-center print:block">
              <p className="text-lg font-semibold">{locationName}</p>
              <p className="text-base text-muted-foreground">{tableName}</p>
            </div>

            <p
              className="break-all text-center text-sm font-medium print:hidden"
              data-testid="printed-qr-url"
            >
              {staticInvite.url}
            </p>

            <div className="flex flex-col gap-2 sm:flex-row print:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={downloading !== null}
                    data-testid="download-qr-button"
                  >
                    <Download className="size-4" />
                    {t("ui.react.pages.staff.location.manage.tables.show.download_qr")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {QR_DOWNLOAD_FORMATS.map((format) => (
                    <DropdownMenuItem
                      key={format}
                      onSelect={() => download(format)}
                      data-testid={`download-qr-option-${format}`}
                    >
                      {t(`ui.react.pages.staff.location.manage.tables.show.download_qr_${format}`)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={revoking}
                    data-testid="revoke-printed-qr-button"
                  >
                    {t("ui.react.pages.staff.location.manage.tables.show.revoke_printed_qr")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("ui.react.pages.staff.location.manage.tables.show.revoke_printed_qr")}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("ui.react.pages.staff.location.manage.tables.show.revoke_printed_qr_warning")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("ui.common.actions.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={revoke}
                      data-testid="confirm-revoke-printed-qr-button"
                    >
                      {t("ui.react.pages.staff.location.manage.tables.show.revoke_printed_qr")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        ) : (
          <div className="space-y-3 print:hidden">
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              {t("ui.react.pages.staff.location.manage.tables.show.no_printed_qr")}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={generate}
              disabled={generating}
              data-testid="generate-printed-qr-button"
            >
              <QrCode className="size-4" />
              {t("ui.react.pages.staff.location.manage.tables.show.generate_printed_qr")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
